import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import ContentBoxItem from "@preferredMedical/Widgets/ContentBoxWithZones/ContentBoxItem";
import React, { useState } from "react";
import { css } from "styled-components";

const enum fields {
    backgroundType = "backgroundType",
    backgroundColor = "backgroundColor",
    gradientStartColor = "gradientStartColor",
    gradientEndColor = "gradientEndColor",
    gradientDirection = "gradientDirection",
    backgroundImage = "backgroundImage",
    imageZoom = "imageZoom",
    subtitleMargin = "subtitleMargin",
    linksMargin = "linksMargin",
    bulletColor = "bulletColor",
    activeBulletColor = "activeBulletColor",
    arrowColor = "arrowColor",
    title = "title",
    subtitle = "subtitle",
    button1Text = "button1Text",
    button1Link = "button1Link",
    button2Text = "button2Text",
    button2Link = "button2Link",
    image = "image",
    items = "items",
}

export type ContentBoxItemType = {
    title: string;
    subtitle: string;
    button1Text: string;
    button1Link: string;
    button2Text: string;
    button2Link: string;
    image: string;
    imageZoom: number;
    subtitleMargin: number;
    linksMargin: number;
    bulletColor: string;
    activeBulletColor: string;
    arrowColor: string;
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundType]: string;
        [fields.backgroundColor]: string;
        [fields.gradientEndColor]: string;
        [fields.gradientStartColor]: string;
        [fields.gradientDirection]: string;
        [fields.backgroundImage]: string;
        [fields.imageZoom]: number;
        [fields.subtitleMargin]: number;
        [fields.linksMargin]: number;
        [fields.bulletColor]: string;
        [fields.activeBulletColor]: string;
        [fields.arrowColor]: string;
        [fields.title]: string;
        [fields.subtitle]: string;
        [fields.button1Text]: string;
        [fields.button1Link]: string;
        [fields.button2Text]: string;
        [fields.button2Link]: string;
        [fields.image]: string;
        [fields.items]: ContentBoxItemType[];
    };
}

export interface ContentBoxStyles {
    mainGridContainer: GridContainerProps;
}

export const contentBoxStyles: ContentBoxStyles = {
    mainGridContainer: {
        gap: 10,
        css: css`
            background-color: ${fields.backgroundColor};
        `,
    },
};

export const ContentBoxContainer = ({ fields }: OwnProps) => {
    const [itemNumber, setItemNumber] = useState(1);
    const totalItems = fields.items ? fields.items.length : 0; 

    const styles = {
        mainGridContainer: {
            ...contentBoxStyles.mainGridContainer,
            css: css`
                background: ${
                    fields.backgroundType === "gradient"
                        ? `linear-gradient(${
                              fields.gradientDirection === "leftToRight"
                                  ? "to right"
                                  : "to bottom"
                          }, ${fields.gradientStartColor}, ${fields.gradientEndColor})`
                        : fields.backgroundType === "image"
                        ? `url(${fields.backgroundImage}) center/cover no-repeat`
                        : fields.backgroundColor
                };
            `,
        },
    };            

    const showNextItem = () => {
        if (itemNumber + 1 > fields.items.length) {
            setItemNumber(1);
        } else {
            setItemNumber(itemNumber + 1);
        }
    };

    const showPreviousItem = () => {
        if (itemNumber - 1 === 0) {
            setItemNumber(fields.items.length);
        } else {
            setItemNumber(itemNumber - 1);
        }
    };

    const onSelectItem = (index: number) => {
        setItemNumber(index + 1);
    };    

    return (
        <GridContainer {...styles.mainGridContainer}>
            <ContentBoxItem
                itemFields={{
                    ...fields.items[itemNumber - 1],
                    imageZoom: fields.imageZoom,
                    subtitleMargin: fields.subtitleMargin,
                    linksMargin: fields.linksMargin       
                }}
                onShowNextItem={showNextItem}
                onShowPreviousItem={showPreviousItem}
                activeItem={itemNumber} 
                onSelectItem={onSelectItem}
                totalItems={totalItems}
                bulletColor={fields.bulletColor}
                activeBulletColor={fields.activeBulletColor}
                arrowColor={fields.arrowColor}
            />
        </GridContainer>
    );
};

const backgroundTypeOptions = [
    { value: "color", displayName: "Color" },
    { value: "gradient", displayName: "Gradient" },
    { value: "image", displayName: "Image" },
];

const gradientDirectionOptions = [
    { value: "leftToRight", displayName: "Left to Right" },
    { value: "topToBottom", displayName: "Top to Bottom" }
];

const widgetModule: WidgetModule = {
    component: ContentBoxContainer,
    definition: {
        group: "Basic",
        displayName: "Content Box Container",
        fieldDefinitions: [
            {
                name: fields.backgroundType,
                displayName: "Background Type",
                editorTemplate: "DropDownField",
                options: backgroundTypeOptions,
                defaultValue: "color",
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "white",
                isVisible: (item: HasFields) => item.fields[fields.backgroundType] === "color"
            },
            {
                name: fields.gradientStartColor,
                displayName: "Gradient Start Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "white",
                fieldType: "General",
                isVisible: (item: HasFields) => item.fields[fields.backgroundType] === "gradient"
            },
            {
                name: fields.gradientEndColor,
                displayName: "Gradient End Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "black",
                fieldType: "General",
                isVisible: (item: HasFields) => item.fields[fields.backgroundType] === "gradient"
            },
            {
                name: fields.gradientDirection,
                displayName: "Gradient Direction",
                editorTemplate: "DropDownField",
                options: gradientDirectionOptions,
                defaultValue: "leftToRight",
                fieldType: "General",
                isVisible: (item: HasFields) => item.fields[fields.backgroundType] === "gradient"
            },
            {
                name: fields.backgroundImage,
                displayName: "Background Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields.backgroundType === "image",
            },
            {
                fieldType: "General",
                name: fields.bulletColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "#0072BC",
            },
            {
                fieldType: "General",
                name: fields.activeBulletColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "white",
            },
            {
                fieldType: "General",
                name: fields.arrowColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "black",
            },
            {
                name: fields.imageZoom,
                displayName: "Image Zoom",
                editorTemplate: "IntegerField",
                defaultValue: 100, 
                fieldType: "General",
            },
            {
                name: fields.subtitleMargin,
                displayName: "Subtitle Margin",
                editorTemplate: "IntegerField",
                defaultValue: 30,
                fieldType: "General",
            }, 
            {
                name: fields.linksMargin,
                displayName: "Links Margin",
                editorTemplate: "IntegerField",
                defaultValue: 30,
                fieldType: "General",
            },                       
            {
                name: fields.items,
                displayName: "Items",
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                hideAdd: false,
                hideEdit: false,
                hideDelete: false,
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: fields.title,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.subtitle,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.button1Text,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.button1Link,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.button2Text,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.button2Link,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.image,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
